/**
 * @file Pages.jsx
 * @description Top-level page orchestrator.
 *
 * Responsibilities (only these — nothing else):
 *   1. Wire feature hooks together
 *   2. Render routes
 *   3. Conditionally render the EULA modal
 *
 * It does NOT: fetch tokens, manage timers, call fetch(), render page components.
 * Each of those concerns lives in its own feature hook.
 */

import React from 'react';
import AppRoutes from './pages/auth/routing/AppRoutes';
import EulaModal from './pages/auth/eula/EulaModal';
import { useEula } from './pages/auth/eula/useEula';
import { useUserProfile } from './pages/auth/useUserProfile';
import { useTokenRefresh } from './pages/auth/useTokenRefresh';
import { useAuthEvents } from './pages/auth/useAuthEvents';
import { useAuthInit } from './pages/auth/useAuthInit';
import { useIdleLogout } from './pages/auth/useIdleLogout';
import { useSseToast } from './pages/auth/useSseToast';

const Pages = () => {
  // ── EULA ─────────────────────────────────────────────────────────────────
  const {
    showEulaModal,
    isChecked,
    setIsChecked,
    hasScrolled,
    handleScroll,
    requireEula,
    acceptEula,
  } = useEula();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { fetchUserProfile } = useUserProfile(requireEula);
  const { refreshToken, getTimeUntilRefresh, isNearExpiry } = useTokenRefresh(fetchUserProfile);

  // Wire MSAL events → profile fetch
  useAuthEvents(fetchUserProfile);

  // Bootstrap auth on mount + schedule token refresh
  useAuthInit(fetchUserProfile, refreshToken, getTimeUntilRefresh, isNearExpiry);

  // Idle-logout watchdog
  useIdleLogout();

  // ── SSE → Toast bridge (fires toasts on any page for task status changes) ─
  useSseToast();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <AppRoutes />

      {showEulaModal && (
        <EulaModal
          isChecked={isChecked}
          hasScrolled={hasScrolled}
          onScroll={handleScroll}
          onToggleCheck={() => setIsChecked((v) => !v)}
          onAccept={acceptEula}
        />
      )}
    </>
  );
};

export default Pages;